/**
 * Security agencies — V0.3 marquee Tier 1 module.
 *
 * Society-empanelled outsourced security agencies. Two surface tabs share state:
 *   - this list page  (CRUD + KPIs)
 *   - /agencies/:id   (detail: overview / roster / invoices)
 *
 * Backend contracts (locked with the parallel API agent):
 *   GET    /v1/agencies?is_active=&search=&cursor=&limit= → { items, next_cursor? }
 *   POST   /v1/agencies                                    → SecurityAgency
 *   GET    /v1/agencies/:id                                → +_count
 *   PATCH  /v1/agencies/:id
 *   POST   /v1/agencies/:id/deactivate | /reactivate
 *   DELETE /v1/agencies/:id
 *
 * The "agency-hired guards" KPI + "monthly outflow" estimate are computed
 * client-side from the accumulated rows so we don't add a bespoke /summary
 * endpoint just for the KPI strip.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Shield,
  Plus,
  Edit3,
  Power,
  Filter,
  IndianRupee,
  Phone,
  Mail,
  Building2,
  Users,
  MoreHorizontal,
  Eye,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDate } from '@/lib/format';
import type {
  SecurityAgency,
  AgencyBillingModel,
  Guard
} from './_agency-types';

// ───── form schemas ─────

const AgencyFormSchema = z
  .object({
    name: z.string().trim().min(2, 'Name required').max(160),
    registration_no: z.string().trim().max(80).optional().or(z.literal('')),
    contact_name: z.string().trim().max(120).optional().or(z.literal('')),
    contact_phone: z.string().trim().max(20).optional().or(z.literal('')),
    contact_email: z.string().trim().email().max(160).optional().or(z.literal('')),
    address: z.string().trim().max(500).optional().or(z.literal('')),
    contract_start: z.string().optional().or(z.literal('')),
    contract_end: z.string().optional().or(z.literal('')),
    contracted_guard_count: z.coerce.number().int().min(0).max(10_000).optional(),
    billing_model: z.enum(['per_guard', 'flat']).default('per_guard'),
    monthly_rate_per_guard_inr: z.coerce.number().int().min(0).max(10_000_000).optional(),
    monthly_flat_fee_inr: z.coerce.number().int().min(0).max(100_000_000).optional(),
    notes: z.string().trim().max(1000).optional().or(z.literal(''))
  })
  .refine(
    (d) =>
      d.billing_model !== 'per_guard' ||
      (d.monthly_rate_per_guard_inr !== undefined && d.monthly_rate_per_guard_inr >= 0),
    { message: 'Per-guard rate required', path: ['monthly_rate_per_guard_inr'] }
  )
  .refine(
    (d) =>
      d.billing_model !== 'flat' ||
      (d.monthly_flat_fee_inr !== undefined && d.monthly_flat_fee_inr >= 0),
    { message: 'Flat fee required', path: ['monthly_flat_fee_inr'] }
  );

export type AgencyFormValues = z.infer<typeof AgencyFormSchema>;

const DEFAULT_AGENCY_FORM: AgencyFormValues = {
  name: '',
  registration_no: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  address: '',
  contract_start: '',
  contract_end: '',
  contracted_guard_count: 0,
  billing_model: 'per_guard',
  monthly_rate_per_guard_inr: 0,
  monthly_flat_fee_inr: 0,
  notes: ''
};

// ───── helpers ─────

function formatInr(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

/**
 * Compute the monthly outflow estimate for an agency row.
 *   per_guard → (rate × contracted_count)  (we use contracted_count as a
 *     stable upper bound; the precise "live count" needs a separate fetch
 *     per agency which we deliberately don't issue for the list page).
 *   flat      → flat_fee
 */
function estimateMonthlyOutflow(a: SecurityAgency): number {
  if (!a.is_active) return 0;
  if (a.billing_model === 'flat') return a.monthly_flat_fee_inr ?? 0;
  if (a.billing_model === 'per_guard') {
    return (a.monthly_rate_per_guard_inr ?? 0) * (a.contracted_guard_count ?? 0);
  }
  return 0;
}

// ───── component ─────

export function AgenciesPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchInput, setSearchInput] = useState<string>('');
  const [deactivateTarget, setDeactivateTarget] = useState<SecurityAgency | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SecurityAgency | null>(null);

  // Cursor accumulation for the list.
  const filtersHash = useMemo(
    () => JSON.stringify([tab, searchInput.trim()]),
    [tab, searchInput]
  );
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const listQuery = useQuery({
    queryKey: ['agencies', tab, searchInput, cursor],
    queryFn: () =>
      get<{ items: SecurityAgency[]; next_cursor?: string | null }>('/agencies', {
        params: {
          limit: 50,
          ...(tab === 'active' ? { is_active: true } : {}),
          ...(tab === 'inactive' ? { is_active: false } : {}),
          ...(searchInput.trim().length > 0 ? { search: searchInput.trim() } : {}),
          ...(cursor ? { cursor } : {})
        }
      })
  });
  const paginator = useCursorAccumulator<SecurityAgency>(listQuery.data, cursor, filtersHash);
  const agencies = paginator.items;

  // Side-fetch: a tally of agency-hired guards across the society.
  // Single page is fine for the KPI (society-wide active agency count is small).
  const agencyGuardsQuery = useQuery({
    queryKey: ['guards', 'agency-count'],
    queryFn: () =>
      get<{ items: Guard[]; next_cursor?: string | null }>('/guards', {
        params: { employment_type: 'agency', limit: 500 }
      })
  });
  const agencyGuardsCount = agencyGuardsQuery.data?.items.length ?? 0;

  // KPI strip
  const totalAgencies = agencies.length;
  const activeAgencies = agencies.filter((a) => a.is_active).length;
  const monthlyOutflowEstimate = agencies.reduce(
    (sum, a) => sum + estimateMonthlyOutflow(a),
    0
  );

  // Form state
  const [editing, setEditing] = useState<SecurityAgency | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const form = useForm<AgencyFormValues>({
    resolver: zodResolver(AgencyFormSchema),
    defaultValues: DEFAULT_AGENCY_FORM,
    mode: 'onChange'
  });
  const billingModel = form.watch('billing_model') as AgencyBillingModel;

  const openCreate = (): void => {
    setEditing(null);
    form.reset(DEFAULT_AGENCY_FORM);
    setDialogOpen(true);
  };
  const openEdit = (a: SecurityAgency): void => {
    setEditing(a);
    form.reset({
      name: a.name,
      registration_no: a.registration_no ?? '',
      contact_name: a.contact_name ?? '',
      contact_phone: a.contact_phone ?? '',
      contact_email: a.contact_email ?? '',
      address: a.address ?? '',
      contract_start: a.contract_start ? a.contract_start.slice(0, 10) : '',
      contract_end: a.contract_end ? a.contract_end.slice(0, 10) : '',
      contracted_guard_count: a.contracted_guard_count ?? 0,
      billing_model: a.billing_model ?? 'per_guard',
      monthly_rate_per_guard_inr: a.monthly_rate_per_guard_inr ?? 0,
      monthly_flat_fee_inr: a.monthly_flat_fee_inr ?? 0,
      notes: a.notes ?? ''
    });
    setDialogOpen(true);
  };

  const save = useMutation({
    mutationFn: (v: AgencyFormValues) => {
      const body = {
        name: v.name,
        registration_no: v.registration_no?.trim() || undefined,
        contact_name: v.contact_name?.trim() || undefined,
        contact_phone: v.contact_phone?.trim() || undefined,
        contact_email: v.contact_email?.trim() || undefined,
        address: v.address?.trim() || undefined,
        contract_start: v.contract_start ? v.contract_start : undefined,
        contract_end: v.contract_end ? v.contract_end : undefined,
        contracted_guard_count: v.contracted_guard_count ?? undefined,
        billing_model: v.billing_model,
        monthly_rate_per_guard_inr:
          v.billing_model === 'per_guard' ? v.monthly_rate_per_guard_inr ?? 0 : undefined,
        monthly_flat_fee_inr:
          v.billing_model === 'flat' ? v.monthly_flat_fee_inr ?? 0 : undefined,
        notes: v.notes?.trim() || undefined
      };
      if (editing) return patch<SecurityAgency>(`/agencies/${editing.id}`, body);
      return post<SecurityAgency>('/agencies', body);
    },
    onSuccess: () => {
      toast.success(editing ? 'Agency updated' : 'Agency created');
      setDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['agencies'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => post<SecurityAgency>(`/agencies/${id}/deactivate`, {}),
    onSuccess: () => {
      toast.success('Agency deactivated');
      void queryClient.invalidateQueries({ queryKey: ['agencies'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const reactivate = useMutation({
    mutationFn: (id: string) => post<SecurityAgency>(`/agencies/${id}/reactivate`, {}),
    onSuccess: () => {
      toast.success('Agency reactivated');
      void queryClient.invalidateQueries({ queryKey: ['agencies'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<unknown>(`/agencies/${id}`),
    onSuccess: () => {
      toast.success('Agency deleted');
      void queryClient.invalidateQueries({ queryKey: ['agencies'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const toggleMenu = (id: string): void => setOpenMenuId((cur) => (cur === id ? null : id));

  // ───── form dialog ─────

  const formDialog = (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit agency' : 'Add security agency'}</DialogTitle>
          <DialogDescription>
            Track the contracting agency, billing model, and roster cap.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          className="flex flex-col gap-vt-3"
        >
          {/* Identity */}
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="a-name">Agency name</Label>
              <Input id="a-name" placeholder="SecureGuard Services" {...form.register('name')} />
              {form.formState.errors.name ? (
                <p className="text-vt-12 text-vt-alert">{form.formState.errors.name.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="a-reg">Registration #</Label>
              <Input id="a-reg" placeholder="PSARA/2024/0123" {...form.register('registration_no')} />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="a-contact-name">Contact person</Label>
              <Input id="a-contact-name" placeholder="Manoj Verma" {...form.register('contact_name')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="a-contact-phone">Contact phone</Label>
              <Input id="a-contact-phone" placeholder="+91…" {...form.register('contact_phone')} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="a-contact-email">Contact email</Label>
              <Input id="a-contact-email" type="email" placeholder="ops@agency.com" {...form.register('contact_email')} />
              {form.formState.errors.contact_email ? (
                <p className="text-vt-12 text-vt-alert">{form.formState.errors.contact_email.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="a-address">Address</Label>
              <Input id="a-address" {...form.register('address')} />
            </div>
          </div>

          {/* Contract */}
          <fieldset className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
            <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Contract
            </legend>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="a-start">Start date</Label>
                <Input id="a-start" type="date" {...form.register('contract_start')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="a-end">End date</Label>
                <Input id="a-end" type="date" {...form.register('contract_end')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="a-count">Contracted guards</Label>
                <Input
                  id="a-count"
                  type="number"
                  min={0}
                  {...form.register('contracted_guard_count')}
                />
              </div>
            </div>
          </fieldset>

          {/* Billing */}
          <fieldset className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
            <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Billing
            </legend>
            <div className="flex flex-wrap items-center gap-vt-4">
              <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
                <input
                  type="radio"
                  value="per_guard"
                  {...form.register('billing_model')}
                  className="accent-vt-primary"
                />
                Per guard
              </label>
              <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
                <input
                  type="radio"
                  value="flat"
                  {...form.register('billing_model')}
                  className="accent-vt-primary"
                />
                Flat monthly fee
              </label>
            </div>
            {billingModel === 'per_guard' ? (
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="a-rate">Monthly rate per guard (₹)</Label>
                <Input
                  id="a-rate"
                  type="number"
                  min={0}
                  {...form.register('monthly_rate_per_guard_inr')}
                />
                {form.formState.errors.monthly_rate_per_guard_inr ? (
                  <p className="text-vt-12 text-vt-alert">
                    {form.formState.errors.monthly_rate_per_guard_inr.message}
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="a-flat">Monthly flat fee (₹)</Label>
                <Input
                  id="a-flat"
                  type="number"
                  min={0}
                  {...form.register('monthly_flat_fee_inr')}
                />
                {form.formState.errors.monthly_flat_fee_inr ? (
                  <p className="text-vt-12 text-vt-alert">
                    {form.formState.errors.monthly_flat_fee_inr.message}
                  </p>
                ) : null}
              </div>
            )}
          </fieldset>

          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="a-notes">Notes</Label>
            <Textarea id="a-notes" rows={2} {...form.register('notes')} />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={save.isPending}
              loadingText="Saving…"
              disabled={save.isPending || !form.formState.isValid}
            >
              {editing ? 'Save changes' : 'Create agency'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <PageHeader
        title="Security agencies"
        description="Contracted security partners — roster caps, billing, monthly outflow."
        actions={
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4" /> Add agency
          </Button>
        }
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-2 xl:grid-cols-4">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <Shield className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Total agencies</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {listQuery.isLoading ? <Skeleton className="h-7 w-12" /> : totalAgencies}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]">
                <Shield className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Active agencies</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {listQuery.isLoading ? <Skeleton className="h-7 w-12" /> : activeAgencies}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-info-100)] text-vt-info">
                <Users className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Agency-hired guards</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {agencyGuardsQuery.isLoading ? <Skeleton className="h-7 w-12" /> : agencyGuardsCount}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-warn-100)] text-vt-warn">
                <IndianRupee className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Monthly outflow (est.)</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {listQuery.isLoading ? (
                    <Skeleton className="h-7 w-20" />
                  ) : (
                    formatInr(monthlyOutflowEstimate)
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="flex flex-wrap items-center gap-vt-3 p-vt-4">
            <TabsPills
              value={tab}
              onChange={(v) => setTab(v as 'all' | 'active' | 'inactive')}
              options={[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
              aria-label="Agency status filter"
            />
            <Filter className="ml-vt-2 h-4 w-4 text-vt-text-muted" />
            <div className="flex-1 min-w-[220px]">
              <Input
                placeholder="Search agency name, contact, registration…"
                className="h-8 text-vt-12"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-vt-4">
            {listQuery.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : listQuery.isError ? (
              <EmptyState
                icon={<Shield className="h-6 w-6" />}
                title="Could not load agencies"
                description={getErrorMessage(listQuery.error)}
                action={
                  <Button variant="secondary" onClick={() => void listQuery.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : agencies.length === 0 ? (
              <EmptyState
                icon={<Shield className="h-6 w-6" />}
                title="No agencies yet"
                description="Add a security agency to track contracted roster size, billing, and monthly outflow."
                action={
                  <Button onClick={openCreate}>
                    <Plus className="h-4 w-4" /> Add agency
                  </Button>
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agency</TableHead>
                    <TableHead>Registration #</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Contract</TableHead>
                    <TableHead className="text-right">Contracted</TableHead>
                    <TableHead className="text-right">Est. monthly</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agencies.map((a) => (
                    <TableRow
                      key={a.id}
                      onClick={() => void navigate({ to: '/agencies/$id', params: { id: a.id } })}
                      className="cursor-pointer hover:bg-vt-surface-2"
                    >
                      <TableCell>
                        <div className="flex items-center gap-vt-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-vt-sm bg-vt-primary-100 text-vt-primary">
                            <Building2 className="h-4 w-4" strokeWidth={1.75} />
                          </span>
                          <div className="min-w-0">
                            <div className="font-medium text-vt-text">{a.name}</div>
                            <div className="text-vt-12 text-vt-text-muted">
                              {a.contact_name ?? '—'}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-vt-13 text-vt-text-muted">
                        {a.registration_no ?? '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col text-vt-12 text-vt-text-muted">
                          {a.contact_phone ? (
                            <span className="inline-flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {a.contact_phone}
                            </span>
                          ) : null}
                          {a.contact_email ? (
                            <span className="inline-flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {a.contact_email}
                            </span>
                          ) : null}
                          {!a.contact_phone && !a.contact_email ? '—' : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-vt-12 text-vt-text-muted">
                        {a.contract_start || a.contract_end ? (
                          <>
                            {a.contract_start ? formatDate(a.contract_start) : '—'} →{' '}
                            {a.contract_end ? formatDate(a.contract_end) : '—'}
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-right text-vt-text-muted">
                        {a.contracted_guard_count ?? '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                        {formatInr(estimateMonthlyOutflow(a))}
                      </TableCell>
                      <TableCell>
                        {a.is_active ? (
                          <Badge variant="ok">Active</Badge>
                        ) : (
                          <Badge variant="muted">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="relative inline-block">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="More actions"
                            onClick={() => toggleMenu(a.id)}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                          {openMenuId === a.id ? (
                            <div
                              className="absolute right-0 z-10 mt-1 w-44 overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface shadow-vt-2"
                              onMouseLeave={() => setOpenMenuId(null)}
                            >
                              <button
                                type="button"
                                className="flex w-full items-center gap-vt-2 px-vt-3 py-vt-2 text-left text-vt-13 text-vt-text hover:bg-vt-surface-2"
                                onClick={() => {
                                  setOpenMenuId(null);
                                  void navigate({ to: '/agencies/$id', params: { id: a.id } });
                                }}
                              >
                                <Eye className="h-4 w-4" /> View detail
                              </button>
                              <button
                                type="button"
                                className="flex w-full items-center gap-vt-2 px-vt-3 py-vt-2 text-left text-vt-13 text-vt-text hover:bg-vt-surface-2"
                                onClick={() => {
                                  setOpenMenuId(null);
                                  openEdit(a);
                                }}
                              >
                                <Edit3 className="h-4 w-4" /> Edit
                              </button>
                              {a.is_active ? (
                                <button
                                  type="button"
                                  className="flex w-full items-center gap-vt-2 px-vt-3 py-vt-2 text-left text-vt-13 text-vt-alert hover:bg-vt-surface-2"
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    setDeactivateTarget(a);
                                  }}
                                >
                                  <Power className="h-4 w-4" /> Deactivate
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="flex w-full items-center gap-vt-2 px-vt-3 py-vt-2 text-left text-vt-13 text-vt-text hover:bg-vt-surface-2"
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    reactivate.mutate(a.id);
                                  }}
                                >
                                  <RotateCcw className="h-4 w-4" /> Reactivate
                                </button>
                              )}
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <CursorPaginator
              hasMore={Boolean(paginator.nextCursor)}
              loading={listQuery.isFetching}
              itemsShown={agencies.length}
              pageSize={50}
              onLoadMore={() => {
                if (paginator.nextCursor) setCursor(paginator.nextCursor);
              }}
            />
          </CardContent>
        </Card>
      </div>

      {formDialog}

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        onOpenChange={(v) => (v ? null : setDeactivateTarget(null))}
        title="Deactivate agency"
        description={
          deactivateTarget ? (
            <>
              Deactivate <strong>{deactivateTarget.name}</strong>? Their guards remain in the
              roster (audit-preserved) but the agency is hidden from active workflows and no new
              invoices can be generated.
            </>
          ) : null
        }
        confirmLabel="Deactivate"
        variant="danger"
        loading={deactivate.isPending}
        onConfirm={() => {
          if (!deactivateTarget) return;
          deactivate.mutate(deactivateTarget.id, {
            onSuccess: () => setDeactivateTarget(null)
          });
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(v) => (v ? null : setDeleteTarget(null))}
        title="Delete agency"
        description={
          deleteTarget ? (
            <>
              Permanently delete <strong>{deleteTarget.name}</strong>? Only allowed when the
              agency has no guards or invoices on file.
            </>
          ) : null
        }
        confirmLabel="Delete"
        variant="danger"
        typeToConfirm="DELETE"
        loading={remove.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          remove.mutate(deleteTarget.id, {
            onSuccess: () => setDeleteTarget(null)
          });
        }}
      />
    </>
  );
}
